import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ListItem } from '../models/ListItem.model';
import { user } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';

@Injectable({ providedIn: 'root' })
export class ListSettingsService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private authService = inject(AuthService);

  private apiUrl = environment.apiUrl + '/ListSettings/';

  // Cache-Speicher für die Listeninhalte
  private cache = new Map<string, ListItem[]>();

  constructor() {}

  // Holt alle verfügbaren Listen (Kategorien) für das Dropdown.
  // Rückgabe: Array von Objekten mit { id, name }

  getAllListSettings(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}`).pipe(
      catchError((err) => {
        console.error('BACKENDFEHLER beim Laden der Listen:', err);
        return of([]);
      }),
    );
  }

  // Holt die Einträge einer spezifischen Liste anhand des Namens.
  // Nutzt lokales Caching für bessere Performance.
  // excludeReservedTypes: blendet reservierte Kern-Aktivitätstypen (z.B. "Notiz") aus -
  // nur für Auswahl-Dropdowns gedacht, nicht für die Listenverwaltung.

  getListByName(
    name: string,
    excludeReservedTypes = false,
  ): Observable<ListItem[]> {
    const cacheKey = excludeReservedTypes ? `${name}::filtered` : name;

    // Falls im Cache vorhanden, sofort zurückgeben
    if (this.cache.has(cacheKey)) {
      return of(this.cache.get(cacheKey)!);
    }

    const url = excludeReservedTypes
      ? `${this.apiUrl}ByName/${name}?excludeReservedTypes=true`
      : `${this.apiUrl}ByName/${name}`;

    return this.http.get<ListItem[]>(url).pipe(
      tap((items) => this.cache.set(cacheKey, items)), // In Cache speichern
      catchError((err) => {
        console.error(`Fehler beim Laden der Liste ${name}:`, err);
        return of([]);
      }),
    );
  }

  // Erstellt einen neuen Eintrag oder aktualisiert einen bestehenden.
  // Invaldiert den Cache nach Erfolg.

  saveListValue(item: ListItem): Observable<ListItem> {
    // Cache leeren, damit beim nächsten Aufruf frische Daten geladen werden
    this.clearCache();

    if (item.id) {
      // UPDATE: Bestehender Eintrag (ID vorhanden)
      return this.http.put<ListItem>(`${this.apiUrl}Value/${item.id}`, item);
    } else {
      // CREATE: Neuer Eintrag
      return this.http.post<ListItem>(`${this.apiUrl}Value`, item);
    }
  }

  // Löscht einen Eintrag anhand der ID.

  deleteListValue(id: number): Observable<any> {
    this.clearCache();
    return this.http.delete(`${this.apiUrl}Value/${id}`);
  }

  // Spezialmethode für voucher-Styles mit Fallback-Werten.

  getVoucherStyles(): Observable<ListItem[]> {
    return this.getListByName('Dokumenten Upload Typ', true).pipe(
      map((items) =>
        items.map((item) => ({
          ...item,
          icon: item.icon || 'pi pi-file',
          color: item.color || '#495057',
        })),
      ),
    );
  }

  // Leert den Cache (entweder komplett oder für eine bestimmte Liste).

  clearCache(name?: string) {
    if (name) {
      this.cache.delete(name);
    } else {
      this.cache.clear();
    }
  }
}
